import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const BOROUGHS = [
  {
    name: 'Manhattan',
    text: 'Fast-paced and always full of options. Manhattan singles want a connection that keeps up with a busy life without losing depth along the way.',
  },
  {
    name: 'Brooklyn',
    text: 'Creative, community-minded, and a little more laid-back. Brooklyn singles are looking for someone whose values and everyday interests actually line up with theirs.',
  },
  {
    name: 'Queens',
    text: 'One of the most diverse boroughs in the city, with just as many kinds of people looking for something real. Find someone whose background and outlook fit alongside your own.',
  },
];

const STEPS = [
  {
    title: 'Meet',
    text: 'Build a profile that actually sounds like you, then start discovering New York singles who share your interests and relationship goals, not just your zip code.',
  },
  {
    title: 'Mingle',
    text: 'Skip the guessing game. With Love Diamond and Interests, you can monitor and express changing interest in real time, so conversations start from something real.',
  },
  {
    title: 'Discover',
    text: 'Take it past the first message. Get to know how someone actually thinks, what they care about, and whether the connection holds up outside the app.',
  },
];

const FAQS = [
  {
    q: 'What are the top dating sites in New York?',
    a: "There's no single answer. It depends on what you're looking for in a platform. Compatibility, clarity on mutual interest, and ease of use matter more than app size. SLAMMMM is built around those priorities.",
  },
  {
    q: 'Are there free dating websites in NYC?',
    a: "Yes. SLAMMMM lets you build a profile and start exploring for free; a starting point, not the whole reason to use it. If you're comparing New York dating sites free of charge, that's exactly where SLAMMMM fits.",
  },
  {
    q: 'How can I meet singles in New York City?',
    a: "Online platforms open up people outside your usual circle. SLAMMMM helps with that first step. Figuring out who's worth pursuing is still the part that takes real information.",
  },
  {
    q: 'What should I look for in a New York dating site?',
    a: 'Clarity on compatibility, communication style, and relationship goals, not just a large local user base.',
  },
  {
    q: 'Is online dating in New York City safe?',
    a: "It can be, with the right habits: protect personal information, keep your own pace, meet in public, and use SLAMMMM's available safety tools when you need them.",
  },
  {
    q: 'How much does online dating in New York cost?',
    a: 'SLAMMMM is free to join and start exploring. Building a profile costs nothing.',
  },
];

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What are the top dating sites in New York?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "There's no single answer. It depends on what you're looking for in a platform. Compatibility, clarity on mutual interest, and ease of use matter more than app size. SLAMMMM is built around those priorities.",
      },
    },
    {
      '@type': 'Question',
      name: 'Are there free dating websites in NYC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes. SLAMMMM lets you build a profile and start exploring for free; a starting point, not the whole reason to use it. If you're comparing New York dating sites free of charge, that's exactly where SLAMMMM fits.",
      },
    },
    {
      '@type': 'Question',
      name: 'How can I meet singles in New York City?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Online platforms open up people outside your usual circle. SLAMMMM helps with that first step. Figuring out who's worth pursuing is still the part that takes real information.",
      },
    },
    {
      '@type': 'Question',
      name: 'What should I look for in a New York dating site?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Clarity on compatibility, communication style, and relationship goals, not just a large local user base.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is online dating in New York City safe?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "It can be, with the right habits: protect personal information, keep your own pace, meet in public, and use SLAMMMM's available safety tools when you need them.",
      },
    },
    {
      '@type': 'Question',
      name: 'How much does online dating in New York cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'SLAMMMM is free to join and start exploring. Building a profile costs nothing.',
      },
    },
  ],
});

const SERVICE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Online Dating in New York',
  serviceType: 'Online Dating Service',
  description: "SLAMMMM is an online dating platform for singles in New York City, helping people create profiles, explore potential matches, and connect with others based on compatibility, communication style, and relationship goals.",
  url: 'https://www.slammmm.com/new-york',
  provider: {
    '@type': 'Organization',
    name: 'SLAMMMM',
    url: 'https://www.slammmm.com/',
  },
  areaServed: {
    '@type': 'City',
    name: 'New York City',
    containedInPlace: {
      '@type': 'State',
      name: 'New York',
      containedInPlace: {
        '@type': 'Country',
        name: 'United States',
      },
    },
  },
  audience: {
    '@type': 'Audience',
    audienceType: 'Singles',
  },
  serviceOutput: {
    '@type': 'Thing',
    name: 'Online dating connections',
  },
});

const NewYork: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo
        title="New York Dating Site | Meet Singles in NYC"
        description="Join a top New York dating site and meet singles across Manhattan, Brooklyn, and Queens. Free to browse, easy sign-up, start dating in NYC today."
        canonical="https://www.slammmm.com/new-york"
        schemas={[FAQ_SCHEMA, SERVICE_SCHEMA]}
      />

      {/* Hero */}
      <section className="py-100 breadcrumb_section bg-light border-bottom">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              <div className="section_title">
                <h1 className="text-primary d-block font500 mb-2">The BEST Dating Site in New York City!</h1>
                <h2 className="mb-4">New York Dating That Gives You More Than Options</h2>
              </div>
              <p className="font20 mb-3">
                New York never runs short on people to meet. What it doesn&rsquo;t always make easy is finding someone who actually fits your life. That&rsquo;s the gap a good New York dating site should close.
              </p>
              <p className="font20 mb-4">
                SLAMMMM looks past the surface. Our approach is built around values, lifestyle, communication style, and relationship goals, the things that actually bring two people closer, not just proximity.
              </p>
              <p className="font20 mb-4">
                Meet New York singles with compatibility in mind. Getting started is simple and free.
              </p>
              <Link to="/signup"><button className="btn btn-primary">Join Free Today</button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro / value prop */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5">
              <div className="section_title">
                <h2 className="mb-0">More Matches Don&rsquo;t Mean Better Matches</h2>
              </div>
            </div>
            <div className="col-lg-7">
              <p className="font20 mb-0">
                A city this size can hand you plenty of matches and still leave you no closer to a real connection. <Link to="/">SLAMMMM</Link> helps you discover people based on what actually makes a relationship work, not just how many profiles you can swipe through in a night.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What makes SLAMMMM worth it */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">What Makes SLAMMMM Actually Worth Your Time?</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <div className="card h-100">
                <h3 className="font28 mb-3 pb-3 border-bottom">Look Beyond the First Impression</h3>
                <p className="mb-0 font20">
                  A photo can catch your eye. It won&rsquo;t tell you if you&rsquo;re actually compatible. Look past the picture and into the values, habits, and everyday things that shape a person, that&rsquo;s where real alignment starts.
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card h-100">
                <h3 className="font28 mb-3 pb-3 border-bottom">Look for More Than a Match</h3>
                <p className="mb-0 font20">
                  Matching is easy. Understanding someone takes a little more. SLAMMMM gives you the tools to see how mutual interest actually develops, so you&rsquo;re building something instead of just collecting matches.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Boroughs */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Dating Across New York City</h2>
            </div>
            <p className="font20 mb-0 mt-3">
              The right connection could be a few subway stops away. Explore singles across New York&rsquo;s boroughs and find people who fit your pace, interests, and idea of a meaningful relationship.
            </p>
          </div>

          <div className="row g-4">
            {BOROUGHS.map((borough) => (
              <div className="col-md-6 col-lg-4" key={borough.name}>
                <div className="card h-100">
                  <h3 className="font22 mb-3 pb-2 border-bottom text-primary">{borough.name}</h3>
                  <p className="mb-0 font20">{borough.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/signup"><button className="btn btn-primary">Find Singles Near You</button></Link>
          </div>
        </div>
      </section>

      {/* Meet, Mingle, Discover */}
      <section className="py-100 our_approach_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">Meet, Mingle &amp; Discover What Fits</h2>
            </div>
            <p className="font20 mb-0 mt-3 text-center">
              Online dating in New York can be simple when you know what you&rsquo;re looking for. With SLAMMMM, it starts with you, moves toward compatibility, and leaves room for something real.
            </p>
          </div>

          <div className="row g-4">
            {STEPS.map((step, i) => (
              <div className="col-md-4" key={step.title}>
                <div className="card h-100">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-2">
                    <h3 className="font28 mb-0">{step.title}</h3>
                    <p className="font28 mb-0 text-primary opacity-50">{`0${i + 1}`}</p>
                  </div>
                  <p className="mb-0 font20">{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/signup"><button className="btn btn-primary">Start Dating in New York</button></Link>
          </div>
        </div>
      </section>

      {/* Less guessing */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <div className="section_title">
                <h2 className="mb-0">Less Guessing. More Understanding.</h2>
              </div>
              <p className="font20 mb-0 mt-3">
                New York moves fast, but a real connection shouldn&rsquo;t feel like a guessing game. SLAMMMM&rsquo;s Love Diamond and Interests give you a clearer read on mutual interest as it develops, so you spend less time decoding messages and more time getting to know someone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-100 love_journey_section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center z-3 position-relative">
              <div className="section_title mb-3">
                <h2 className="text-white">Ready to Find a Connection That Fits?</h2>
              </div>
              <p className="text-white font20 mb-4 pb-2">
                New York has plenty of stories waiting to meet yours. Start with curiosity, find what aligns, and see where a connection can go.
              </p>
              <Link to="/signup"><button className="btn btn-primary">Sign Up Free, Meet New York Singles</button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="section_title text-center mb-4">
            <h2 className="mb-0">Locations we serve</h2>
          </div>
          <div className="row g-3 justify-content-center">
            <div className="col-md-4">
              <Link to="/florida" className="btn btn-dark w-100">Florida</Link>
            </div>
            <div className="col-md-4">
              <Link to="/orlando" className="btn btn-dark w-100">Orlando, FL</Link>
            </div>
            <div className="col-md-4">
              <Link to="/new-york" className="btn btn-dark w-100">New York</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-100 help_faq_section" id="new-york-faq">
        <div className="container">
          <div className="section_title text-center mb-5">
            <h2 className="mb-0">Frequently Asked Questions</h2>
          </div>

          <div className="row">
            <div className="col-lg-9 mx-auto">
              <div className="accordion" id="new-york-faq-accordion">
                {FAQS.map((item, index) => (
                  <div className="accordion-item" key={item.q}>
                    <h3 className="accordion-header">
                      <button
                        className={`accordion-button ${index === 0 ? '' : 'collapsed'}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#new-york-faq-${index}`}
                        aria-expanded={index === 0 ? 'true' : 'false'}
                        aria-controls={`new-york-faq-${index}`}
                      >
                        {item.q}
                      </button>
                    </h3>
                    <div
                      id={`new-york-faq-${index}`}
                      className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                      data-bs-parent="#new-york-faq-accordion"
                    >
                      <div className="accordion-body">{item.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NewYork;
